import React from "react";
import parse from "html-react-parser";
import { Resume, Skill, Reference, PROFILE_LABELS, PROFILE_SKILLS } from "../types";
import { useProfile } from "../context/ProfileContext";

type Props = {
  resume: Resume;
};

function PrintSkills({ skills, isSkillRelevant, filtered }: {
  skills: Skill[];
  isSkillRelevant: (s: string) => boolean;
  filtered: boolean;
}) {
  const categories = skills.filter((s) => !s.break && s.type);

  return (
    <div className="print-section">
      <h2 className="print-section-title">Compétences</h2>
      <div className="space-y-2">
        {categories.map((cat) => {
          const regular = cat.items?.find((i) => i.level === "REGULAR")?.items || [];
          const sometimes = cat.items?.find((i) => i.level === "SOMETIMES")?.items || [];
          const all = [...regular, ...sometimes];

          const visible = filtered ? all.filter(isSkillRelevant) : all;
          if (visible.length === 0) return null;

          return (
            <div key={cat.type}>
              <p className="print-cat-label mb-0.5">{cat.type}</p>
              <div className="flex flex-wrap gap-[3px]">
                {visible.map((item) => (
                  <span
                    key={item}
                    className={`print-skill-tag ${regular.includes(item) ? "print-skill-regular" : "print-skill-occasional"}`}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PrintExperience({ entry, isSubEntry = false }: {
  entry: Reference;
  isSubEntry?: boolean;
}) {
  const dateRange = [
    entry.startMonth || entry.startYear
      ? `${entry.startMonth || ""} ${entry.startYear || ""}`.trim()
      : null,
    entry.endMonth || entry.endYear
      ? `${entry.endMonth || ""} ${entry.endYear || ""}`.trim()
      : null,
  ]
    .filter(Boolean)
    .join(" \u2192 ");

  return (
    <div className={`${isSubEntry ? "ml-3 border-l border-neutral-200 pl-3" : ""} print-avoid-break`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <h4 className={`${isSubEntry ? "text-[11px]" : "text-xs"} font-bold text-neutral-900 leading-tight`}>
          {entry.name}
        </h4>
        {dateRange && (
          <span className="text-[9px] text-neutral-400 tabular-nums shrink-0">
            {dateRange}
          </span>
        )}
      </div>
      <p className={`${isSubEntry ? "text-[9px]" : "text-[10px]"} text-neutral-500 leading-tight`}>
        {entry.title}
      </p>

      {entry.context && (
        <p className="text-[9px] text-neutral-500 italic mt-0.5 leading-snug">
          {parse(entry.context)}
        </p>
      )}

      {entry.skills && entry.skills.length > 0 && (
        <p className="text-[9px] text-neutral-400 mt-0.5">
          {entry.skills.join(", ")}
        </p>
      )}

      {entry.subreferences && entry.subreferences.length > 0 && (
        <div className="mt-1.5 space-y-1.5">
          {entry.subreferences.map((sub, i) => (
            <PrintExperience key={i} entry={sub} isSubEntry />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PrintLayout({ resume }: Props) {
  const { profile, isExperienceRelevant } = useProfile();
  const filtered = profile !== "all";

  const profileLabel = PROFILE_LABELS[profile];

  // Filter experiences: show relevant ones, plus education (no skills = always shown)
  const visibleExperiences = filtered
    ? resume.references.filter((ref) => {
        if (!ref.skills || ref.skills.length === 0) {
          // Check subreferences too
          if (ref.subreferences && ref.subreferences.length > 0) {
            return ref.subreferences.some((sub) => isExperienceRelevant(sub.skills));
          }
          return true; // Education entries always shown
        }
        return isExperienceRelevant(ref.skills) ||
          (ref.subreferences && ref.subreferences.some((sub) => isExperienceRelevant(sub.skills)));
      })
    : resume.references;

  const visibleProjects = filtered
    ? resume.projects.filter(
        (p) => isExperienceRelevant(p.skills) ||
          (p.subreferences && p.subreferences.some((sub) => isExperienceRelevant(sub.skills)))
      )
    : resume.projects;

  // Contact info (only useful ones, no LinkedIn icon)
  const contacts = resume.basics.contact.filter(
    (c) => c.name !== "Full Remote" && !c.name.includes("Permis") && !c.imageBrands
  );

  return (
    <div className="print-layout-root">
      {/* Header */}
      <header className="print-header">
        <div className="flex items-center gap-5">
          <img
            src="./profil-image.jpg"
            alt={resume.basics.name}
            className="w-20 h-20 rounded-2xl object-cover shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 leading-none">
              {resume.basics.name}
            </h1>
            <p className="text-sm font-medium text-neutral-500 mt-1">
              {filtered
                ? profileLabel
                : Object.entries(PROFILE_LABELS)
                    .filter(([k]) => k !== "all")
                    .map(([, v]) => v)
                    .join(" · ")}
              <span className="text-neutral-300"> — </span>
              {resume.basics.experience.replace("+", "").toLowerCase()}
            </p>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              {contacts.map((c, i) => (
                <span key={i} className="text-[10px] text-neutral-500 flex items-center gap-1">
                  {c.image && (
                    <i className={`fa ${c.image} text-[9px] text-neutral-400`} />
                  )}
                  {c.link && !c.link.startsWith("mailto:")
                    ? c.name
                    : c.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="print-separator" />

      {/* Two-column body: skills sidebar + experiences */}
      <div className="print-two-col">
        {/* Left column: Skills + Interests (floated sidebar) */}
        <div className="print-col-left">
          <PrintSkills
            skills={resume.skills}
            isSkillRelevant={(s) => {
              if (!filtered) return true;
              return PROFILE_SKILLS[profile as Exclude<typeof profile, "all">]?.includes(s) ?? false;
            }}
            filtered={filtered}
          />

          {resume.interests && resume.interests.length > 0 && (
            <div className="print-section mt-3">
              <h2 className="print-section-title">Centres d'intérêt</h2>
              <p className="text-[9px] text-neutral-500 leading-relaxed">
                {resume.interests.map((i) => i.name).join(" · ")}
              </p>
            </div>
          )}
        </div>

        {/* Right column: Experiences */}
        <div className="print-col-right">
          <div className="print-section">
            <h2 className="print-section-title">Expériences</h2>
            <div className="space-y-3.5">
              {visibleExperiences.map((exp, i) => (
                <PrintExperience key={i} entry={exp} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Projects — outside the two-column area for proper page flow */}
      {visibleProjects.length > 0 && (
        <div className="print-projects-section">
          <h2 className="print-section-title">Projets</h2>
          <div className="space-y-2">
            {visibleProjects.map((proj, i) => (
              <PrintExperience key={i} entry={proj} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
